import { useState } from "react";
import { Navigate, useParams } from "react-router-dom";

import { Avatar, Button, Pill, Tabs, type TabItem } from "@/components/common";
import { Guard } from "@/auth/Guard";
import { PERM } from "@/auth/permissions";
import { useWorkspace } from "@/hooks/useWorkspaces";
import { SplashScreen } from "@/app/SplashScreen";
import { OrganizationsList } from "@/components/organizations/OrganizationsList";
import { EditWorkspaceModal } from "./EditWorkspaceModal";
import { InvitesTab } from "./InvitesTab";
import { MembersTab } from "./MembersTab";
import { RolesTab } from "./RolesTab";
import { WorkspaceOverviewTab } from "./WorkspaceOverviewTab";
import { WorkspaceSettingsTab } from "./WorkspaceSettingsTab";
import "./WorkspaceDetailPage.css";

type TabId = "overview" | "members" | "roles" | "invites" | "orgs" | "settings";

const TABS: TabItem<TabId>[] = [
  { id: "overview", label: "Visão geral", icon: "activity" },
  { id: "members", label: "Membros", icon: "users" },
  { id: "roles", label: "Papéis", icon: "shield-check" },
  { id: "invites", label: "Convites", icon: "mail" },
  { id: "orgs", label: "Organizações", icon: "layers" },
  { id: "settings", label: "Configurações", icon: "settings" },
];

export function WorkspaceDetailPage() {
  const params = useParams();
  const id = Number(params.id);
  const { data: ws, isLoading, isError } = useWorkspace(id);
  const [tab, setTab] = useState<TabId>("overview");
  const [editing, setEditing] = useState(false);

  if (!Number.isFinite(id)) return <Navigate to="/workspaces" replace />;
  if (isLoading) return <SplashScreen />;
  if (isError || !ws) return <Navigate to="/workspaces" replace />;

  return (
    <>
      <div className="ws-detail-hero">
        <Avatar name={ws.name} size="lg" />
        <div className="ws-detail-hero-info">
          <h1 className="page-title">{ws.name}</h1>
          <div className="ws-detail-meta">
            <Pill tone={ws.active ? "ativa" : "pausada"}>{ws.active ? "Ativo" : "Inativo"}</Pill>
            {ws.plan && <span className="td-small">{ws.plan.name}</span>}
            {ws.is_platform_tenant && <span className="td-small">Plataforma TDAX</span>}
          </div>
        </div>
        <Guard permission={PERM.workspacesWrite}>
          <Button variant="ghost" iconLeft="settings" onClick={() => setEditing(true)}>
            Editar
          </Button>
        </Guard>
      </div>

      <Tabs items={TABS} active={tab} onChange={setTab} />

      {tab === "overview" && <WorkspaceOverviewTab workspaceId={ws.id} />}
      {tab === "members" && <MembersTab workspaceId={ws.id} />}
      {tab === "roles" && <RolesTab workspaceId={ws.id} />}
      {tab === "invites" && <InvitesTab workspaceId={ws.id} />}
      {tab === "orgs" && <OrganizationsList workspaceId={ws.id} />}
      {tab === "settings" && <WorkspaceSettingsTab workspaceId={ws.id} />}

      {editing && <EditWorkspaceModal mode="edit" workspace={ws} onClose={() => setEditing(false)} />}
    </>
  );
}
